import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from '@glu/theming';
import { withFormContext } from '@glu/validation-react';

import numeral from 'numeral';
import get from 'lodash/get';
import LocaleContext from '../../locale/LocaleContext';
import { PAYMENT_TYPES } from '../../../constants';

const FormContextWrapper = ({ children, data }) => children({ formData: data });
const FormContext = withFormContext(FormContextWrapper);

const styles = ({ palette: { grey }, spacing: { unit } }) => ({
  root: {
    background: grey.G100,
    border: `1px solid ${grey.A100}`,
    minWidth: 419,
    padding: [unit * 2, unit * 3],
    position: 'fixed',
    right: 4,
    top: '50%',
    zIndex: 20,
    transform: 'translateY(-50%)'
  },
  row: {
    color: grey.G600,
    fontSize: 14,
    marginTop: unit * 1.5
  },
  label: {
    fontWeight: 'bold'
  },
  rowValue: {
    marginLeft: unit / 2
  }
});

const useStyles = createUseStyles(styles);

export const getCalculations = (formState, data = { fxRates: [] }) => {
  const currency =
    data.fxRates.find((x) => x.currency === formState.currency) || get(formState, 'currency.0');
  const amountType = get(formState, 'amountType.0.type');
  const isUSDEquiv = amountType === 'D';
  const type = isUSDEquiv || !amountType ? 'usdequiv' : 'foreign';
  const format = '0,0.00';
  const usd = currency && currency.currency === 'USD';

  return {
    type,
    currency: [currency],
    isUSDEquiv,
    usd,
    usdequiv: {
      sent: usd
        ? numeral(formState.amount).format(format)
        : numeral(
            formState.exchangeRate
              ? numeral(formState.amount).value() / formState.exchangeRate
              : formState.amount
          ).format(format),
      debit: formState.amount
    },
    foreign: {
      sent: formState.amount,
      debit: numeral(numeral(formState.amount).value() * formState.exchangeRate || 1).format(
        format
      )
    }
  };
};

const PaymentInfoComponent = ({ data, formState, state }) => {
  const locale = useContext(LocaleContext);
  const classes = useStyles();

  const currencies = {
    state: get(state, 'currency.0.currency'),
    form: get(formState, 'currency.0.currency')
  };

  const exchange = {
    state: get(state, 'currency.0.exchange'),
    form: get(formState, 'exchangeRate')
  };

  const curr =
    get(formState, 'currency.0') ||
    (state && data.fxRates.find((x) => x.currency === state.currency));
  const international = state && state.paymentTypeStr === PAYMENT_TYPES.WIRE.INTERNATIONAL;
  const usd = curr && curr.currency === locale['common.usd'];

  if (!international || !numeral(state.amount).value() || usd || !currencies.form) {
    return null;
  }

  const updatedCurrency =
    currencies.state !== currencies.form ? currencies.form : currencies.state;
  const updatedExchange = exchange.state !== exchange.form ? exchange.form : exchange.state;

  const details = getCalculations(
    { ...formState, exchangeRate: updatedExchange, currency: updatedCurrency },
    data
  );
  const { currency, type } = details;

  const currencyData = get(currency, '0');
  return (
    <FormContext>
      {({ formData }) => {
        return (
          <div className={classes.root}>
            <h4>Payment Information</h4>

            <div className={classes.row}>
              <span className={classes.label}>Currency:</span>
              <span className={classes.rowValue}>
                {get(currencyData, 'currencyName')} ({get(currencyData, 'currency')})
              </span>
            </div>

            <div className={classes.row}>
              <span className={classes.label}>Exchange Rate:</span>
              <span className={classes.rowValue}>{formData.exchangeRate}</span>
            </div>
            <div className={classes.row}>
              <span className={classes.label}>Amount to be debited:</span>
              <span className={classes.rowValue}>
                {numeral(details[type].debit).format('0,0.00')} USD
              </span>
            </div>
            <div className={classes.row}>
              <span className={classes.label}>Amount to be sent:</span>
              <span className={classes.rowValue}>
                {numeral(details[type].sent).format('0,0.00')} {get(currencyData, 'currency')}
              </span>
            </div>
          </div>
        );
      }}
    </FormContext>
  );
};

PaymentInfoComponent.propTypes = {
  data: PropTypes.shape({}).isRequired,
  state: PropTypes.shape({
    amount: PropTypes.any,
    currency: PropTypes.any,
    exchangeRate: PropTypes.any,
    paymentTypeStr: PropTypes.any
  }).isRequired,
  formState: PropTypes.shape({}).isRequired
};

export default PaymentInfoComponent;
